import {useCallback, useEffect, useRef, useState} from 'react';
import {
    AvailableAnalysisClient,
    useFetchAvailableClientsQuery,
} from '@goproblems/shared/api/analysisClients';
import {AnalysisClientStatus} from '@types';
import {useMercure} from '@utils/hooks/useMercure';
import {useTranslation} from 'react-i18next';

type StatusUpdate = {
    clientId: string;
    name: string;
    isBusy: boolean;
    pendingRequestCount?: number;
};

type Props = {
    value: string | null;
    onChange: (clientId: string | null) => void;
    showStatusLabel?: boolean;
};

const getClientStatus = (
    client: AvailableAnalysisClient,
    busyOverrides: Record<string, boolean>
): AnalysisClientStatus => {
    if (!client.isOnline) return AnalysisClientStatus.OFFLINE;
    if (busyOverrides[client.clientId] ?? client.isBusy)
        return AnalysisClientStatus.BUSY;
    return AnalysisClientStatus.READY;
};

const statusColors: Record<string, string> = {
    [AnalysisClientStatus.OFFLINE]: '#a3a3a3',
    [AnalysisClientStatus.BUSY]: '#eab308',
    [AnalysisClientStatus.READY]: '#22c55e',
};

const getOverallStatus = (
    clients: AvailableAnalysisClient[],
    busyOverrides: Record<string, boolean>
): AnalysisClientStatus => {
    const hasOnline = clients.some(c => c.isOnline);
    if (!hasOnline) return AnalysisClientStatus.OFFLINE;
    const hasFree = clients.some(
        c => c.isOnline && !(busyOverrides[c.clientId] ?? c.isBusy)
    );
    return hasFree ? AnalysisClientStatus.READY : AnalysisClientStatus.BUSY;
};

const statusLabelKeys: Record<string, string> = {
    [AnalysisClientStatus.OFFLINE]: 'analysis_client.none_online',
    [AnalysisClientStatus.BUSY]: 'analysis_client.all_busy',
    [AnalysisClientStatus.READY]: 'analysis_client.available',
};

export const AnalysisClientSelector = ({
    value,
    onChange,
    showStatusLabel = false,
}: Props) => {
    const {t} = useTranslation();
    const {data, isLoading} = useFetchAvailableClientsQuery();
    const clients = data?.clients;
    const [busyOverrides, setBusyOverrides] = useState<Record<string, boolean>>(
        {}
    );
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const handleMercureMessage = useCallback((data: unknown) => {
        const message = data as {type?: string; payload?: StatusUpdate};
        const update = message?.payload ?? (data as StatusUpdate);
        if (update?.clientId) {
            setBusyOverrides(prev => ({
                ...prev,
                [update.clientId]: update.isBusy,
            }));
        }
    }, []);

    useMercure({
        topic: '/analysis-clients/status',
        onMessage: handleMercureMessage,
    });

    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(e.target as Node)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () =>
            document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const overallStatus = clients
        ? getOverallStatus(clients, busyOverrides)
        : 'offline';

    const selectedClient = clients?.find(c => c.clientId === value);
    const selectedName = selectedClient?.name ?? t('common.automatic');

    const displayStatus = selectedClient
        ? getClientStatus(selectedClient, busyOverrides)
        : overallStatus;

    return (
        <div className="relative inline-flex items-center" ref={containerRef}>
            <button
                type="button"
                className="flex items-center gap-1.5 px-1 py-0.5 rounded hover:bg-[var(--ui-surface-active)] transition-colors"
                onClick={() => setIsOpen(prev => !prev)}
                title={t(statusLabelKeys[displayStatus])}>
                <span
                    className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{
                        backgroundColor: statusColors[displayStatus],
                    }}
                />
                <span className="text-xs text-body-secondary">
                    {selectedName}
                </span>
            </button>

            {isOpen && (
                <div className="absolute top-full left-0 mt-1 z-50 bg-body border border-secondary rounded shadow-lg min-w-[200px] py-1">
                    {showStatusLabel && (
                        <div className="px-3 py-1.5 text-xs text-body-secondary border-b border-secondary">
                            {t(statusLabelKeys[overallStatus])}
                        </div>
                    )}

                    <button
                        type="button"
                        className={`w-full text-left px-3 py-1.5 text-sm hover:bg-[var(--ui-surface-active)] flex items-center gap-2 ${
                            value === null ? 'font-semibold' : ''
                        }`}
                        onClick={() => {
                            onChange(null);
                            setIsOpen(false);
                        }}>
                        <span
                            className="inline-block w-2 h-2 rounded-full"
                            style={{
                                backgroundColor: statusColors[overallStatus],
                            }}
                        />
                        {t('common.automatic')}
                    </button>

                    {!isLoading &&
                        clients?.map(client => {
                            const status = getClientStatus(
                                client,
                                busyOverrides
                            );
                            return (
                                <button
                                    key={client.clientId}
                                    type="button"
                                    className={`w-full text-left px-3 py-1.5 text-sm hover:bg-[var(--ui-surface-active)] flex items-center gap-2 ${
                                        value === client.clientId
                                            ? 'font-semibold'
                                            : ''
                                    }`}
                                    onClick={() => {
                                        onChange(client.clientId);
                                        setIsOpen(false);
                                    }}>
                                    <span
                                        className="inline-block w-2 h-2 rounded-full"
                                        style={{
                                            backgroundColor:
                                                statusColors[status],
                                        }}
                                    />
                                    {client.name}
                                    {status !== AnalysisClientStatus.READY && (
                                        <span className="text-xs text-body-secondary ml-auto">
                                            {status ===
                                            AnalysisClientStatus.OFFLINE
                                                ? t('analysis_client.offline')
                                                : t('analysis_client.busy')}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                </div>
            )}
        </div>
    );
};
